import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Goal, Handshake, ShieldCheck, Square, RefreshCw, Filter } from "lucide-react";
import { toast } from "sonner";

const BOARDS = [
  { key: "scorers", label: "Top Scorers", icon: Goal, unit: "goals", accent: "text-primary" },
  { key: "assists", label: "Assists", icon: Handshake, unit: "assists", accent: "text-[hsl(var(--stadium-yellow))]" },
  { key: "clean_sheets", label: "Clean Sheets", icon: ShieldCheck, unit: "CS", accent: "text-[hsl(var(--pitch-green))]" },
  { key: "yellow", label: "Yellow Cards", icon: Square, unit: "YC", accent: "text-[hsl(var(--stadium-yellow))]" },
  { key: "red", label: "Red Cards", icon: Square, unit: "RC", accent: "text-destructive" },
];

function relTime(iso) {
  if (!iso) return "not yet";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Chip({ color, short }) {
  return (
    <span className="inline-flex items-center justify-center w-6 h-5 text-[9px] font-bold text-white shrink-0"
      style={{ backgroundColor: color || "#555" }}>{short}</span>
  );
}

function LeaderboardCard({ board, rows }) {
  const Icon = board.icon;
  return (
    <div className="bg-card border border-border" data-testid={`stats-card-${board.key}`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b-2 border-foreground">
        <Icon size={16} className={board.accent} />
        <h3 className="font-display text-lg uppercase tracking-wide">{board.label}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-muted-foreground font-data" data-testid={`stats-empty-${board.key}`}>No data yet</p>
      ) : (
        <div>
          {rows.slice(0, 10).map((r, i) => (
            <div key={`${r.team_id}-${r.player}-${i}`} data-testid={`stats-row-${board.key}-${i}`}
              className="flex items-center gap-3 px-4 py-2 border-t border-border first:border-t-0">
              <span className="font-display text-base w-5 text-center text-muted-foreground">{i + 1}</span>
              <Chip color={r.color} short={r.team} />
              <span className="flex-1 min-w-0 truncate text-sm font-medium">{r.player}</span>
              <span className={`font-data font-bold text-lg ${board.accent}`}>{r.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function StatsView({ leagueId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [myClubOnly, setMyClubOnly] = useState(false);
  const favId = user?.favorite_team_id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leagues/${leagueId}/stats`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    toast.info("Crunching player stats…", { description: "This runs in the background and can take a minute." });
    try {
      await api.post(`/admin/aggregate-stats?league_id=${leagueId}`);
      // poll a few times for updated_at to change
      const before = data?.updated_at;
      for (let i = 0; i < 8; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: fresh } = await api.get(`/leagues/${leagueId}/stats`);
        if (fresh.updated_at && fresh.updated_at !== before) { setData(fresh); break; }
        setData(fresh);
      }
      toast.success("Leaderboards refreshed");
    } catch {
      toast.error("Couldn't refresh stats", { description: "The data provider may be rate-limiting." });
    } finally {
      setRefreshing(false);
    }
  };

  const hasAny = data && BOARDS.some((b) => (data[b.key] || []).length > 0);
  const rowsFor = (key) => {
    const rows = data?.[key] || [];
    return myClubOnly && favId ? rows.filter((r) => r.team_id === favId) : rows;
  };

  return (
    <div data-testid="stats-view">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="overline text-primary">Season leaderboards · 2026/27</p>
          <p className="text-xs text-muted-foreground font-data">Updated {relTime(data?.updated_at)}</p>
        </div>
        {isAdmin && (
          <button onClick={refresh} disabled={refreshing} data-testid="stats-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-border text-xs uppercase tracking-wider hover:border-primary/60 hover:text-foreground transition-colors disabled:opacity-60">
            <RefreshCw size={13} className={refreshing ? "animate-spin text-primary" : ""} />
            {refreshing ? "Refreshing…" : "Refresh stats"}
          </button>
        )}
        {favId && (
          <button onClick={() => setMyClubOnly((v) => !v)} data-testid="stats-myclub-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs uppercase tracking-wider transition-colors ${
              myClubOnly ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <Filter size={13} /> {myClubOnly ? "My club only" : "All clubs"}
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-center text-sm text-muted-foreground py-10">Loading…</p>
      ) : !hasAny ? (
        <div className="bg-card border border-border p-8 text-center" data-testid="stats-season-empty">
          <ShieldCheck size={28} className="mx-auto text-muted-foreground mb-3" />
          <h3 className="font-display text-2xl uppercase">The season hasn't kicked off</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Every player starts on zero for 2026/27. Top scorers, assists, clean sheets and cards
            fill in automatically as real matches are played.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {BOARDS.map((b) => <LeaderboardCard key={b.key} board={b} rows={rowsFor(b.key)} />)}
        </div>
      )}
    </div>
  );
}
